import { useEffect, useRef } from 'react';

interface UseAutoSearchOptions {
  initialSearchTerm?: string;
  currentEndpoint?: { tableName?: string } | null;
  onServerSearch?: (searchTerm: string) => Promise<void>;
  setSearchTerm: (term: string) => void;
}

/**
 * Custom hook to handle automatic search triggering for initial search terms
 * (e.g., from home page cards like "Genealogy")
 * 
 * This hook:
 * 1. Applies the initial search term when endpoint changes
 * 2. Auto-triggers server search for comprehensive results
 * 3. Prevents infinite loops with ref-based tracking
 * 4. Clears search when no initial term is provided
 */
export const useAutoSearch = ({
  initialSearchTerm,
  currentEndpoint,
  onServerSearch,
  setSearchTerm
}: UseAutoSearchOptions) => {
  // Track whether we've already applied the initial search term to prevent infinite loops
  const hasAppliedInitialSearchRef = useRef(false);
  const lastAppliedEndpointRef = useRef<string | null>(null);

  useEffect(() => {
    const currentEndpointKey = currentEndpoint?.tableName || null;
    
    // Track endpoint changes but DON'T reset the applied flag
    // This prevents re-applying the initial search term on every endpoint change
    if (lastAppliedEndpointRef.current !== currentEndpointKey) {
      lastAppliedEndpointRef.current = currentEndpointKey;
      console.log('[useAutoSearch] Endpoint changed to:', currentEndpointKey);
    }
    
    // Only apply initial search term once, when first initialized
    if (currentEndpoint && initialSearchTerm && !hasAppliedInitialSearchRef.current) {
      console.log('[useAutoSearch] First initialization - applying initial search term:', initialSearchTerm);
      setSearchTerm(initialSearchTerm);
      hasAppliedInitialSearchRef.current = true;
      
      // Automatically trigger server search for initial search terms (e.g., Genealogy card)
      if (onServerSearch) {
        console.log('[useAutoSearch] Auto-triggering server search for initial term:', initialSearchTerm);
        // Small delay to ensure search term is set first
        setTimeout(() => {
          onServerSearch(initialSearchTerm);
        }, 100);
      }
    }
  }, [currentEndpoint?.tableName, setSearchTerm, initialSearchTerm, onServerSearch]);

  return {
    // Could return additional state/methods if needed in the future
    isInitialized: hasAppliedInitialSearchRef.current
  };
}; 